import React, { useRef, useState } from "react";
import OcButton from "../components/OcButton";
import WaitlistModal from "../components/WaitlistModal";
import useOcReveal from "../useOcReveal";
import "./Hero.scss";

import glassBox from "../../../assets/images/home/01-hero/hero-text-glass-boxes.png";
import gradySitting from "../../../assets/images/home/01-hero/grady-sitting.webp";

import iconMomentum from "../../../assets/images/home/01-hero/icon-momentum.svg";
import iconRealPerk from "../../../assets/images/home/01-hero/icon-real-perk.svg";
import iconTrackRecord from "../../../assets/images/home/01-hero/icon-track-record.svg";

// Raw photos (no baked-in rounding/padding) — the Photo frame applies the
// rounded corners and cover crop.
import photo1 from "../../../assets/images/home/01-hero/hero-photo-1.webp";
import photo2 from "../../../assets/images/home/01-hero/hero-photo-2.webp";
import photo3 from "../../../assets/images/home/01-hero/hero-photo-3.webp";
import photo4 from "../../../assets/images/home/01-hero/hero-photo-4.webp";
import photo5 from "../../../assets/images/home/01-hero/hero-photo-5.webp";
import photo6 from "../../../assets/images/home/01-hero/hero-photo-6.webp";
import photo7 from "../../../assets/images/home/01-hero/hero-photo-7.webp";
import photo8 from "../../../assets/images/home/01-hero/hero-photo-8.webp";
import photo9 from "../../../assets/images/home/01-hero/hero-photo-9.webp";

// Kick off hero photo fetches as soon as this module evaluates (before paint).
const HERO_PHOTOS = [
  photo1,
  photo2,
  photo3,
  photo4,
  photo5,
  photo6,
  photo7,
  photo8,
  photo9,
  gradySitting,
];
if (typeof window !== "undefined") {
  HERO_PHOTOS.forEach((src) => {
    if (document.head.querySelector(`link[rel="preload"][href="${src}"]`)) return;
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = src;
    document.head.appendChild(link);
  });
}

// The photo covers its frame (object-fit: cover), preserving aspect ratio.
// `objectPosition` lets a photo shift its focal point when the default centre
// crop isn't ideal. Above-the-fold: eager + high priority (never lazy).
const Photo = ({ src, tall, alt = "", objectPosition }) => (
  <div className={`hero-photo${tall ? " hero-photo--tall" : ""}`}>
    <img
      src={src}
      alt={alt}
      loading="eager"
      fetchPriority="high"
      decoding="async"
      style={objectPosition ? { objectPosition } : undefined}
    />
  </div>
);

const GlassLabel = ({ children }) => (
  <div className="hero-glass" style={{ backgroundImage: `url(${glassBox})` }}>
    <span>{children}</span>
  </div>
);

const InfoCard = ({ icon, title, children }) => (
  <div className="hero-card">
    <div className="hero-card__icon">
      <img src={icon} alt="" aria-hidden="true" />
    </div>
    <div className="hero-card__text">
      <p className="hero-card__title">{title}</p>
      <p className="hero-card__desc">{children}</p>
    </div>
  </div>
);

export default function Hero() {
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const sectionRef = useRef(null);
  // Prototype restart: header stays, then copy / CTAs / collage dissolve in.
  useOcReveal(sectionRef, { when: "mount" });

  return (
    <section className="hero" id="hero" ref={sectionRef}>
      {/* Rectangle 22256 — soft blue glow at the bottom (CSS, not an image) */}
      <span className="hero__glow" aria-hidden="true" />

      <div className="hero__headline oc-reveal">
        <div className="hero__headline-text">
          <h1>Learn AI by building real things, with people who push you forward</h1>
          <p>
            You've been experimenting, learning, building in the margins. You can feel how big this is.
            Open Campus gives you the people, the tools, and the structure to turn that energy into something real.
          </p>
        </div>
        <div className="hero__cta">
          <OcButton
            variant="outline"
            href="#tools"
            onClick={(e) => {
              const el = document.querySelector("#tools");
              if (!el) return;
              e.preventDefault();
              el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
          >
            Tools to support you
          </OcButton>
          <OcButton variant="primary" onClick={() => setWaitlistOpen(true)}>
            Join the first AI Fast Track cohort
          </OcButton>
        </div>
      </div>

      {/* Collage — columns positioned relative to viewport centre to match Figma */}
      <div className="hero-col hero-col--a oc-reveal" style={{ "--oc-delay": "0.18s" }}>
        <Photo src={photo1} tall alt="Open Campus builders" />
        <Photo src={photo2} alt="Open Campus community" />
        <GlassLabel>Millions reached</GlassLabel>
      </div>

      <div className="hero-col hero-col--b oc-reveal" style={{ "--oc-delay": "0.18s" }}>
        <Photo src={photo3} alt="AI builders" />
        <GlassLabel>AI Builders</GlassLabel>
        <InfoCard icon={iconMomentum} title="Real momentum">
          This AI chapter is just getting started. Join now, and you're not just a member, you help shape what it becomes.
        </InfoCard>
        <Photo src={photo4} alt="" />
      </div>

      <div className="hero-col hero-col--c oc-reveal" style={{ "--oc-delay": "0.18s" }}>
        <Photo src={photo5} tall alt="Guided workshop" />
        <GlassLabel>Guided Workshops</GlassLabel>
        <InfoCard icon={iconTrackRecord} title="Real track record">
          From hackathons to mentorship, OC has built and supported real learners and builders since we started. Now we're bringing that same energy into AI.
        </InfoCard>
      </div>

      <div className="hero-col hero-col--d oc-reveal" style={{ "--oc-delay": "0.18s" }}>
        <Photo src={photo6} alt="" />
        <InfoCard icon={iconRealPerk} title="Real perks">
          Partners and creators building alongside you. They provide a solid foundation by bringing new connections, and exclusive perks into the community.
        </InfoCard>
        <GlassLabel>Multiple Markets</GlassLabel>
        <Photo src={photo7} alt="" />
      </div>

      <div className="hero-col hero-col--e oc-reveal" style={{ "--oc-delay": "0.18s" }}>
        <Photo src={photo8} tall alt="Community event" />
        <GlassLabel>Community</GlassLabel>
        <Photo src={photo9} alt="" />
      </div>

      <div className="hero__grady oc-reveal" style={{ "--oc-delay": "0.28s" }}>
        <img
          src={gradySitting}
          alt="Grady mascot"
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
      </div>

      <WaitlistModal
        open={waitlistOpen}
        onClose={() => setWaitlistOpen(false)}
        variant="fastTrack"
      />
    </section>
  );
}
